import { AddToGroupIcon, BranchTimelineIcon } from '@icons';

import { ReactComponent as CurrencyIcon } from '@assets/icons/currency-icon.svg';
import { ReactComponent as NoteIcon } from '@assets/icons/edit-file.svg';
import { ReactComponent as AttachmentIcon } from '@assets/icons/file-history.svg';
import { ReactComponent as PhoneCallIcon } from '@assets/icons/phone-call.svg';
import { ReactComponent as UserIcon } from '@assets/icons/user.svg';
import { ReactComponent as EventIcon } from '@components/icon/icons/event-timeline.svg';
import { ReactComponent as TagIcon } from '@components/icon/icons/tag.svg';
import { ReactComponent as TaskIcon } from '@components/icon/icons/task-timeline.svg';
import { capitalizeFirstLetter } from '@utils/stringFormat';

import {
  EntityConfig,
  EntityType,
  ModifiedDataKey,
  TimelineAction,
  TimelineEntity
} from '../types/leadActivity';

export enum TaskStatusEnum {
  SCHEDULED = 'scheduled',
  COMPLETED = 'completed',
  OVERDUE = 'overdue'
}

export const TASK_STATUS_LABEL: Record<TaskStatusEnum, string> = {
  [TaskStatusEnum.SCHEDULED]: 'Scheduled',
  [TaskStatusEnum.COMPLETED]: 'Completed',
  [TaskStatusEnum.OVERDUE]: 'Overdue'
};

export const TASK_COLOR_CLASS: Record<TaskStatusEnum, string> = {
  [TaskStatusEnum.SCHEDULED]: 'bg-[#F17F0C12] text-[#F17F0C]',
  [TaskStatusEnum.COMPLETED]: 'bg-[#E5F5EC] text-[#47B881]',
  [TaskStatusEnum.OVERDUE]: 'bg-[#FEEEEB] text-[#C94122]'
};

export enum TaskTypeEnum {
  FOLLOW_UP = 'follow_up',
  DEMO_BOOKED = 'demo_booked',
  SETUP_DEMO = 'setup_demo',
  SEND_PROPOSAL = 'send_proposal'
}

export const TASK_TYPE_LABEL: Record<TaskTypeEnum, string> = {
  [TaskTypeEnum.FOLLOW_UP]: 'Follow Up',
  [TaskTypeEnum.DEMO_BOOKED]: 'Demo Booked',
  [TaskTypeEnum.SETUP_DEMO]: 'Setup Demo',
  [TaskTypeEnum.SEND_PROPOSAL]: 'Send Proposal'
};

export const LEAD_ACTIVITY_LIMIT = 10;

export const ENTITY_ICON_MAP = {
  [TimelineEntity.LEAD_ACTIVITY_CALL]: PhoneCallIcon,
  [TimelineEntity.LEAD_ATTACHMENTS]: AttachmentIcon,
  [TimelineEntity.LEAD]: UserIcon,
  [TimelineEntity.LEAD_ACTIVITY_TASK]: TaskIcon,
  [TimelineEntity.LEAD_ACTIVITY_EVENT]: EventIcon,
  [TimelineEntity.LEAD_NOTES]: NoteIcon,
  [TimelineEntity.LEAD_TAGS_MAP]: TagIcon,
  [TimelineEntity.PAYMENT_ORDER]: CurrencyIcon,
  [TimelineEntity.GROUP_MEMBER]: AddToGroupIcon,
  [TimelineEntity.BRANCH_MEMBER]: BranchTimelineIcon
} as const;

export const ENTITY_FORMAT_MAP = {
  [TimelineEntity.LEAD]: 'Lead',
  [TimelineEntity.LEAD_ACTIVITY_CALL]: 'Call',
  [TimelineEntity.LEAD_ACTIVITY_TASK]: 'Task',
  [TimelineEntity.LEAD_ACTIVITY_EVENT]: 'Meeting',
  [TimelineEntity.LEAD_NOTES]: 'Notes',
  [TimelineEntity.LEAD_ATTACHMENTS]: 'Attachments',
  [TimelineEntity.LEAD_TAGS_MAP]: 'Tags',
  [TimelineEntity.PAYMENT_ORDER]: 'Payment Order',
  [TimelineEntity.GROUP_MEMBER]: 'Group Member',
  [TimelineEntity.BRANCH_MEMBER]: 'Branch Member'
};

export const ACTION_TYPE_MAP = {
  [TimelineAction.CREATE]: 'created',
  [TimelineAction.UPDATE]: 'updated',
  [TimelineAction.DELETE]: 'deleted',
  [TimelineAction.SYNC]: 'synced',
  [TimelineAction.MARK_PAID]: 'marked as paid',
  [TimelineAction.DISABLE]: 'disabled',
  [TimelineAction.CLOSE]: 'closed',
  [TimelineAction.SEND_REMINDER]: 'reminder sent',
  [TimelineAction.ADD_DISCOUNT]: 'discount applied',
  [TimelineAction.ENABLE_SPLIT_PAYMENT]: 'enabled partial payment'
};

export const ENTITY_TITLE_OVERRIDE: Partial<
  Record<TimelineEntity, Partial<Record<TimelineAction, string>>>
> = {
  [TimelineEntity.LEAD]: {
    [TimelineAction.SYNC]: 'Lead synced'
  },
  [TimelineEntity.PAYMENT_ORDER]: {
    [TimelineAction.CREATE]: 'Payment link sent',
    [TimelineAction.MARK_PAID]: 'Payment marked as paid',
    [TimelineAction.DISABLE]: 'Payment link disabled',
    [TimelineAction.SEND_REMINDER]: 'Payment reminder sent',
    [TimelineAction.ADD_DISCOUNT]: 'Discount applied',
    [TimelineAction.ENABLE_SPLIT_PAYMENT]: 'Enabled partial payment'
  },
  [TimelineEntity.BRANCH_MEMBER]: {
    [TimelineAction.CREATE]: 'Added to branch'
  },
  [TimelineEntity.GROUP_MEMBER]: {
    [TimelineAction.CREATE]: 'Added to Group',
    [TimelineAction.UPDATE]: 'Group details updated'
  }
};

export const LEAD_FIELD_CONFIGS = [
  { key: ModifiedDataKey.USERNAME, displayName: 'Username' },
  { key: ModifiedDataKey.EMAIL, displayName: 'Email' },
  { key: 'phone', displayName: 'Phone number' },
  {
    key: ModifiedDataKey.PHONE_COUNTRY_CODE,
    displayName: 'Phone number country code'
  },
  { key: 'phone_secondary', displayName: 'Secondary phone number' },
  {
    key: 'phone_secondary_country_code',
    displayName: 'Secondary phone number country code'
  },
  { key: ModifiedDataKey.LOCATION, displayName: 'Location' },
  { key: ModifiedDataKey.CATEGORY, displayName: 'Category' },
  { key: ModifiedDataKey.SOURCE, displayName: 'Source' },
  { key: ModifiedDataKey.STATUS, displayName: 'Status' },
  { key: ModifiedDataKey.MOBILE_NUMBER, displayName: 'Mobile number' },
  { key: ModifiedDataKey.DEAL_VALUE, displayName: 'Deal value' },
  { key: ModifiedDataKey.PRIORITY, displayName: 'Priority' },
  { key: ModifiedDataKey.DESCRIPTION, displayName: 'Description' },
  { key: ModifiedDataKey.ASSIGNEE, displayName: 'Assignee' },
  { key: ModifiedDataKey.STATE, displayName: 'State' },
  { key: ModifiedDataKey.CITY, displayName: 'City' }
] as const;

export const ENTITY_CONFIGS: Record<EntityType, EntityConfig> = {
  call: {
    fields: [
      ModifiedDataKey.DATE,
      ModifiedDataKey.TYPE,
      ModifiedDataKey.OUTCOME,
      ModifiedDataKey.REMARKS
    ],
    createMessage: (typeData) =>
      typeData?.current_value
        ? `${typeData.current_value} call was added`
        : 'Call was added',
    fieldFormatter: (field) => field.charAt(0).toUpperCase() + field.slice(1)
  },
  task: {
    fields: [
      ModifiedDataKey.NAME,
      ModifiedDataKey.DATE,
      ModifiedDataKey.TYPE,
      ModifiedDataKey.STATUS,
      ModifiedDataKey.REMARKS
    ],
    createMessage: (nameData) =>
      nameData?.current_value
        ? `${nameData.current_value} was added`
        : 'Task was added',
    fieldFormatter: (field) => field.charAt(0).toUpperCase() + field.slice(1)
  },
  event: {
    fields: [
      ModifiedDataKey.NAME,
      ModifiedDataKey.START_DATE,
      ModifiedDataKey.END_DATE,
      ModifiedDataKey.LOCATION,
      ModifiedDataKey.REMARKS
    ],
    createMessage: (nameData) =>
      nameData?.current_value
        ? `${nameData.current_value} meeting is added`
        : 'Meeting is added',
    fieldFormatter: (field) =>
      field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ')
  }
};

export const createMessageConfigs = [
  {
    entity: TimelineEntity.LEAD,
    key: ModifiedDataKey.USERNAME,
    template: '{value} was added',
    fallback: 'Lead was added'
  },
  {
    entity: TimelineEntity.LEAD_ACTIVITY_CALL,
    key: ModifiedDataKey.TYPE,
    template: '{value} call was added',
    fallback: 'Call was added'
  },
  {
    entity: TimelineEntity.LEAD_ACTIVITY_TASK,
    key: ModifiedDataKey.NAME,
    template: '{value} was added',
    fallback: 'Task was added'
  },
  {
    entity: TimelineEntity.LEAD_ACTIVITY_EVENT,
    key: ModifiedDataKey.NAME,
    template: '{value} meeting is added',
    fallback: 'Meeting is added'
  },
  {
    entity: TimelineEntity.LEAD_NOTES,
    key: ModifiedDataKey.NOTE,
    template: 'Note "{value}" was added',
    fallback: 'Note was added'
  },
  {
    entity: TimelineEntity.LEAD_TAGS_MAP,
    key: ModifiedDataKey.TAG,
    template: 'Tag {value} was added',
    fallback: 'Tag was added'
  },
  {
    entity: TimelineEntity.LEAD_ATTACHMENTS,
    key: ModifiedDataKey.DOCUMENT,
    template: 'Attachment {value} was added',
    fallback: 'Attachment was added'
  },
  {
    entity: TimelineEntity.BRANCH_MEMBER,
    key: ModifiedDataKey.BRANCH_MEMBER,
    template: '{value}',
    fallback: ''
  },
  {
    entity: TimelineEntity.GROUP_MEMBER,
    key: ModifiedDataKey.GROUP_MEMBER,
    template: '{value}',
    fallback: ''
  }
];

export const CallTypeOptions = [
  { id: 'inbound', label: 'Inbound', value: 'inbound' },
  { id: 'outbound', label: 'Outbound', value: 'outbound' }
];

export const Outcome = [
  { id: 'interested', label: 'Interested', value: 'interested' },
  { id: 'not_interested', label: 'Not Interested', value: 'not_interested' },
  { id: 'follow_up', label: 'Follow Up', value: 'follow_up' },
  { id: 'did_not_pick', label: 'Did Not Pick', value: 'did_not_pick' },
  {
    id: 'not_able_to_connect',
    label: 'Not Able to Connect',
    value: 'not_able_to_connect'
  },
  { id: 'switch_off', label: 'Switch Off', value: 'switch_off' },
  { id: 'disqualified', label: 'Disqualified', value: 'disqualified' },
  { id: 'invalid_number', label: 'Invalid Number', value: 'invalid_number' }
];

export const TaskStatus = [
  {
    id: TaskStatusEnum.SCHEDULED,
    label: TASK_STATUS_LABEL[TaskStatusEnum.SCHEDULED],
    value: TaskStatusEnum.SCHEDULED
  },
  {
    id: TaskStatusEnum.COMPLETED,
    label: TASK_STATUS_LABEL[TaskStatusEnum.COMPLETED],
    value: TaskStatusEnum.COMPLETED
  },
  {
    id: TaskStatusEnum.OVERDUE,
    label: TASK_STATUS_LABEL[TaskStatusEnum.OVERDUE],
    value: TaskStatusEnum.OVERDUE
  }
];

export const TaskTypeOptions = [
  {
    id: TaskTypeEnum.FOLLOW_UP,
    label: TASK_TYPE_LABEL[TaskTypeEnum.FOLLOW_UP],
    value: TaskTypeEnum.FOLLOW_UP
  },
  {
    id: TaskTypeEnum.DEMO_BOOKED,
    label: TASK_TYPE_LABEL[TaskTypeEnum.DEMO_BOOKED],
    value: TaskTypeEnum.DEMO_BOOKED
  },
  {
    id: TaskTypeEnum.SETUP_DEMO,
    label: TASK_TYPE_LABEL[TaskTypeEnum.SETUP_DEMO],
    value: TaskTypeEnum.SETUP_DEMO
  },
  {
    id: TaskTypeEnum.SEND_PROPOSAL,
    label: TASK_TYPE_LABEL[TaskTypeEnum.SEND_PROPOSAL],
    value: TaskTypeEnum.SEND_PROPOSAL
  }
];

export enum ActivityTypeOptions {
  TASK = 'task',
  EVENT = 'event',
  CALL = 'call'
}

export const getTypeLabel = (type: string) => {
  switch (type) {
    case 'event':
      return 'Meeting';
    case 'call':
      return 'Call Info';
    default:
      return capitalizeFirstLetter(type);
  }
};

export const callColumns = [
  {
    Header: 'Call Info Type',
    accessor: 'type',
    style: 'font-medium text-gray-900'
  },
  {
    Header: 'Date',
    accessor: 'timestamp',
    style: 'text-center'
  },
  {
    Header: 'Time',
    accessor: 'time',
    style: 'text-center'
  },
  {
    Header: 'Outcome',
    accessor: 'outcome',
    style: 'text-center'
  },
  {
    Header: 'Actions',
    accessor: 'actions',
    style: 'text-center'
  }
];

export const eventColumns = [
  {
    Header: 'Meeting Name',
    accessor: 'name',
    style: 'font-medium text-gray-900'
  },
  {
    Header: 'Date',
    accessor: 'timestamp',
    style: 'text-center'
  },
  {
    Header: 'Start Time',
    accessor: 'startTime',
    style: 'text-center'
  },
  {
    Header: 'End Time',
    accessor: 'endTime',
    style: 'text-center'
  },
  {
    Header: 'Actions',
    accessor: 'actions',
    style: 'text-center'
  }
];

export const taskColumns = [
  {
    Header: 'Task Name',
    accessor: 'name',
    style: 'font-medium text-gray-900'
  },
  {
    Header: 'Type',
    accessor: 'task_type',
    style: 'text-center'
  },
  {
    Header: 'Date',
    accessor: 'timestamp',
    style: 'text-center'
  },
  {
    Header: 'Time',
    accessor: 'time',
    style: 'text-center'
  },
  {
    Header: 'Status',
    accessor: 'status',
    style: 'text-center'
  },
  {
    Header: 'Actions',
    accessor: 'actions',
    style: 'text-center'
  }
];
